import { presentLexError } from '@/lib/user-notice';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getContentZoom, setContentZoom, ZOOM_FONT_SIZES, type ContentZoom } from '@/lib/contentZoom';

export function ProfilePage() {
  const { user, logout, changePassword } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [contentZoom, setContentZoomState] = useState<ContentZoom>(getContentZoom);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword.trim()) {
      setPasswordError('Indica tu contraseña actual.');
      return;
    }
    if (!newPassword.trim()) {
      setPasswordError('Indica la nueva contraseña.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('La nueva contraseña y la confirmación no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess('Contraseña actualizada correctamente.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(closePasswordModal, 1500);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Error al cambiar contraseña.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="lex-page">
      {/* Page title */}
      <div style={{ padding: '4px 0 16px' }}>
        <p style={{ margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
          Cuenta
        </p>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--color-text)' }}>
          Perfil
        </h1>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* User info */}
        <div style={{ padding: '16px 0', borderBottom: '1px solid var(--color-border-soft)' }}>
          <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
            Usuario
          </p>
          <p style={{ margin: '0 0 2px', fontSize: 17, fontWeight: 600, color: 'var(--color-text)' }}>
            {user?.name ?? ''}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            {user?.email ?? ''}
          </p>
        </div>

        {/* Security row */}
        <button
          type="button"
          onClick={openPasswordModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 0',
            background: 'none',
            border: 'none',
            borderBottom: '1px solid var(--color-border-soft)',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <div>
            <p style={{ margin: '0 0 1px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Seguridad
            </p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>
              Cambiar contraseña
            </p>
          </div>
          <i className="ri-arrow-right-s-line" style={{ fontSize: 18, color: 'var(--color-text-muted)', flexShrink: 0 }} aria-hidden />
        </button>

        {/* Theme toggle row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--color-border-soft)' }}>
          <div>
            <p style={{ margin: '0 0 1px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Interfaz
            </p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>
              Tema oscuro
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isDark}
            onClick={toggleTheme}
            style={{
              width: 48,
              height: 28,
              borderRadius: 20,
              border: 'none',
              background: isDark ? 'var(--color-navy)' : 'var(--color-border)',
              cursor: 'pointer',
              position: 'relative',
              flexShrink: 0,
              transition: 'background 0.2s',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 3,
                left: isDark ? 23 : 3,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: isDark ? 'var(--color-bg)' : '#ffffff',
                transition: 'left 0.2s ease',
              }}
            />
          </button>
        </div>

        {/* Content font size row — affects Leyes & Clasifica# only */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--color-border-soft)' }}>
          <div>
            <p style={{ margin: '0 0 1px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Interfaz
            </p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>
              Tamaño de texto
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--color-text-muted)' }}>
              Leyes y Clasifica#
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['small', 'medium', 'large'] as ContentZoom[]).map(size => (
              <button
                key={size}
                type="button"
                onClick={() => { setContentZoom(size); setContentZoomState(size); }}
                style={{
                  width: 38, height: 38, borderRadius: 9, cursor: 'pointer',
                  fontWeight: 700, lineHeight: 1,
                  fontSize: ZOOM_FONT_SIZES[size],
                  background: contentZoom === size ? 'var(--color-navy)' : 'var(--color-surface)',
                  color: contentZoom === size ? 'var(--color-bg)' : 'var(--color-text-muted)',
                  border: contentZoom === size ? 'none' : '1px solid var(--color-border)',
                  transition: 'background 0.15s, color 0.15s',
                } as React.CSSProperties}
                aria-label={`Texto ${size}`}
                aria-pressed={contentZoom === size}
              >
                A
              </button>
            ))}
          </div>
        </div>

        {/* Support row */}
        <a
          href="mailto:info@dumas.solutions"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 0',
            borderBottom: '1px solid var(--color-border-soft)',
            textDecoration: 'none',
          }}
        >
          <div>
            <p style={{ margin: '0 0 1px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Soporte
            </p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>
              info@dumas.solutions
            </p>
          </div>
          <i className="ri-external-link-line" style={{ fontSize: 16, color: 'var(--color-text-muted)', flexShrink: 0 }} aria-hidden />
        </a>

        {/* Logout */}
        <div style={{ padding: '24px 0 8px' }}>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: '11px 16px',
              borderRadius: 10,
              border: '1px solid var(--color-danger)',
              background: 'transparent',
              color: 'var(--color-danger)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <i className="ri-logout-box-r-line" aria-hidden />
            Cerrar sesión
          </button>
        </div>
      </div>

      {/* Password change modal */}
      {showPasswordModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(7,15,28,0.54)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: 20,
          }}
          onClick={closePasswordModal}
        >
          <div
            style={{
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: 12,
              padding: 24,
              maxWidth: 400,
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
              Seguridad
            </p>
            <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600, color: 'var(--color-text)' }}>
              Cambiar contraseña
            </h2>
            <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label htmlFor="current-password" style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                  Contraseña actual
                </label>
                <input id="current-password" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} disabled={submitting} className="lex-input" />
              </div>
              <div>
                <label htmlFor="new-password" style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                  Nueva contraseña
                </label>
                <input id="new-password" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={submitting} className="lex-input" />
              </div>
              <div>
                <label htmlFor="confirm-password" style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                  Confirmación
                </label>
                <input id="confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={submitting} className="lex-input" />
              </div>
              {passwordError ? <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 14 }}>{presentLexError(passwordError)}</p> : null}
              {passwordSuccess ? <p style={{ margin: 0, color: 'var(--lex-success)', fontSize: 14 }}>{passwordSuccess}</p> : null}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button type="button" className="lex-button-secondary" onClick={closePasswordModal} disabled={submitting}>
                  Cancelar
                </button>
                <button type="submit" className="lex-button-primary" disabled={submitting}>
                  {submitting ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </main>
  );
}
